import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import _isNil from "lodash/isNil";
import SunEditor from "suneditor-react";
import { LabeledText } from "./LabeledText";
import { LabeledTextContext } from "../../withPORevision";
import InputLoader from "../ui/Loader/InputLoader";
import { removeControlCharacters } from "./utils";
import { sanitizePastedHtml } from "./sanitizePastedHtml";

// Styled wrapper for SunEditor
const StyledEditorWrapper = styled.div`
  & .sun-editor {
    font-family: "Open Sans", sans-serif;
    border: 1px solid
      ${props => (props.error ? "#f44336" : "rgba(0, 0, 0, 0.23)")};
    border-radius: 4px;
    transition: border-color 0.2s;

    &:hover {
      border-color: ${props =>
        props.error ? "#f44336" : "rgba(0, 0, 0, 0.87)"};
    }

    &:focus-within {
      border-color: ${props => (props.error ? "#f44336" : "#3f51b5")};
      border-width: 2px;
    }
  }

  & .se-wrapper {
    min-height: ${props => props.minHeight || "200px"};
  }

  & .se-status-bar {
    display: none;
  }

  ${props =>
    props.required &&
    `
    & .sun-editor::before {
      content: "*";
      color: #f44336;
      position: absolute;
      right: 10px;
      top: 10px;
      font-size: 20px;
      font-weight: bold;
    }
  `}
`;

const Label = styled.label`
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  color: ${props => (props.error ? "#f44336" : "rgba(0, 0, 0, 0.54)")};
  margin-bottom: 4px;
  display: block;
  transition: color 0.2s;
`;

const HelperText = styled.div`
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  color: ${props => (props.error ? "#f44336" : "rgba(0, 0, 0, 0.54)")};
  margin-top: 4px;
  min-height: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HelperTextContent = styled.span`
  flex: 1;
`;

const CharacterCounter = styled.span`
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  color: ${props => (props.overLimit ? "#f44336" : "rgba(0, 0, 0, 0.54)")};
  font-weight: ${props => (props.overLimit ? "600" : "normal")};
  margin-left: 8px;
  white-space: nowrap;
`;

const getTextContent = html => {
  if (!html) return "";
  if (typeof html !== "string") return "";

  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

const truncateTextNode = (node, charCount, maxLength) => {
  const remaining = maxLength - charCount.value;
  const text = node.textContent || "";
  if (text.length <= remaining) {
    charCount.value += text.length;
    return node.cloneNode(true);
  }
  charCount.value = maxLength;
  return document.createTextNode(text.substring(0, remaining));
};

const truncateElementNode = (node, { charCount, maxLength, truncateFn }) => {
  const clone = node.cloneNode(false);
  for (
    let i = 0;
    i < node.childNodes.length && charCount.value < maxLength;
    i++
  ) {
    const child = truncateFn(node.childNodes[i], charCount, maxLength);
    if (child) clone.appendChild(child);
  }
  return clone;
};

const truncateNode = (node, charCount, maxLength) => {
  if (charCount.value >= maxLength) return null;
  const handlers = {
    [Node.TEXT_NODE]: () => truncateTextNode(node, charCount, maxLength),
    [Node.ELEMENT_NODE]: () =>
      truncateElementNode(node, {
        charCount,
        maxLength,
        truncateFn: truncateNode,
      }),
  };
  return (handlers[node.nodeType] || (() => null))();
};

const buildTruncatedContainer = (source, maxLength) => {
  const charCount = { value: 0 };
  const result = document.createElement("DIV");
  Array.from(source.childNodes).forEach(node => {
    if (charCount.value >= maxLength) return;
    const child = truncateNode(node, charCount, maxLength);
    if (child) result.appendChild(child);
  });
  return result;
};

const truncateHtml = (html, maxLength) => {
  if (!html || typeof html !== "string") return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return buildTruncatedContainer(tmp, maxLength).innerHTML;
};

const DisabledInput = ({ label, value }) => {
  return <LabeledText label={label} texts={[getTextContent(value)]} />;
};

DisabledInput.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
};

const getError = (error, errorMessage) => (error ? errorMessage : null);

const parseEvent = (content, name, omitControlChars) => {
  const stringContent = String(content || "");
  const parsedValue = removeControlCharacters(stringContent, omitControlChars);
  const finalValue = parsedValue === "" ? null : parsedValue;
  return {
    target: {
      name,
      value: finalValue,
    },
  };
};

const createOnChange = (handlers, config) => content => {
  const parsedEvent = parseEvent(content, config.name, config.omitControlChars);
  handlers.onChange(parsedEvent);
  handlers.handleChange && handlers.handleChange(parsedEvent);
};

export const useLabeledText = ignoreLabeledText => {
  const labeledText = useContext(LabeledTextContext);
  return labeledText && !ignoreLabeledText;
};

const focusAndBlurEditor = editorCore => {
  const wysiwyg = editorCore.context?.element?.wysiwyg;
  if (!wysiwyg) return;

  editorCore.focus();
  wysiwyg.blur();
};

const initializeEditor = (editor, getSunEditorInstance, onEditorReady) => {
  if (getSunEditorInstance) {
    getSunEditorInstance(editor);
  }

  if (!editor.core?.context) return;

  setTimeout(() => {
    focusAndBlurEditor(editor.core);
    if (onEditorReady) {
      onEditorReady(editor);
    }
  }, 50);
};

const useEditorInitialization = (
  getSunEditorInstance,
  onEditorReady,
  editorRef
) => {
  const [isEditorReady, setIsEditorReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsEditorReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isEditorReady || !editorRef.current?.editor) return;

    const editor = editorRef.current.editor;
    initializeEditor(editor, getSunEditorInstance, onEditorReady);
  }, [isEditorReady, getSunEditorInstance, onEditorReady, editorRef]);

  return isEditorReady;
};

export const getHelperText = config => {
  const errorCaption = getError(config.error, config.errorMessage);
  if (config.ignoreCaption) {
    return errorCaption || null;
  }
  return errorCaption || config.helperText || " ";
};

const handleBlur = ({
  setLocalValue,
  handler,
  onBlur,
  timerRef,
  omitControlChars,
  name,
}) => (evt, editorContents) => {
  const content = String(editorContents || "");
  const parsedEvent = parseEvent(content, name, omitControlChars);

  if (onBlur) {
    const res = onBlur(parsedEvent);
    if (res && res.hasError) return;
  }
  setLocalValue(content);
  handler(content);
  timerRef.current.isFlushed = true;
};

const handleDebouncedHandler = ({ timerRef, handler, evt, delay }) => {
  if (timerRef.current.id) clearTimeout(timerRef.current.id);
  timerRef.current.id = setTimeout(() => {
    if (timerRef.current.isFlushed) {
      return;
    }
    handler(evt);
  }, delay || 1500);
  timerRef.current.isFlushed = false;
};

const executeHandler = (processedContent, context) => {
  if (context.avoidOnChange) return;

  if (context.avoidDebounce) {
    context.handler(processedContent);
    return;
  }

  handleDebouncedHandler({
    timerRef: context.timerRef,
    handler: context.handler,
    evt: processedContent,
    delay: context.delay,
  });
};

const handleLocalChange = ({
  setLocalValue,
  avoidDebounce,
  avoidOnChange,
  handler,
  timerRef,
  delay,
  isValid,
  setCharCount,
}) => content => {
  setCharCount(getTextContent(content).length);
  if (!isValid(content)) return;

  setLocalValue(content);
  executeHandler(content, {
    avoidOnChange,
    avoidDebounce,
    handler,
    timerRef,
    delay,
  });
};

const handlePaste = ({ limit, setCharCount }) => (
  _e,
  cleanData,
  _maxCharCount,
  core
) => {
  const sanitized = sanitizePastedHtml(cleanData);

  if (!limit) {
    core.functions.insertHTML(sanitized, true, false);
    return false;
  }

  const currentLength = getTextContent(core.getContents(false)).length;
  const remaining = limit - currentLength;
  const sanitizedLength = getTextContent(sanitized).length;
  const fits = currentLength + sanitizedLength <= limit;

  if (fits) {
    core.functions.insertHTML(sanitized, true, false);
    return false;
  }

  if (remaining > 0) {
    core.functions.insertHTML(truncateHtml(sanitized, remaining), true, false);
    setCharCount(limit);
  }
  return false;
};

const defaultButtonList = [
  [
    "fontSize",
    "bold",
    "underline",
    "italic",
    "strike",
    "subscript",
    "superscript",
    "list",
  ],
];

const buildEditorOptions = ({
  minHeight,
  placeholder,
  sunEditorOptions,
  limit,
}) => ({
  buttonList: defaultButtonList,
  mode: "classic",
  minHeight: minHeight || "200px",
  height: "auto",
  placeholder: placeholder || "",
  ...(limit ? { charCounter: true, charCounterMax: limit } : {}),
  ...sunEditorOptions,
});

const syncValueWithLimit = ({
  value,
  limit,
  setLocalValue,
  setCharCount,
  handler,
}) => {
  const stringValue = String(value || "");
  const textLength = getTextContent(stringValue).length;
  if (limit && textLength > limit) {
    const truncated = truncateHtml(stringValue, limit);
    setLocalValue(truncated);
    setCharCount(limit);
    handler(truncated);
    return;
  }
  setLocalValue(stringValue);
  setCharCount(textLength);
};

const renderLabel = (label, error, required) => {
  if (!label) return null;
  return (
    <Label error={error} required={required}>
      {label}
    </Label>
  );
};

const renderCharacterCounter = (showCounter, limit, charCount) => {
  if (!limit) return null;
  return (
    <CharacterCounter overLimit={charCount > limit}>
      {limit - charCount} characters left
    </CharacterCounter>
  );
};

const RichTextInputWithError = ({
  name,
  label,
  placeholder,
  value,
  error,
  errorMessage,
  ignoreCaption,
  helperText,
  onChange,
  handleChange,
  labeledTextProps,
  ignoreLabeledText,
  avoidDebounce,
  delay,
  onBlur,
  isDisabled,
  avoidOnChange,
  isValid = () => true,
  omitControlChars = true,
  minHeight,
  required,
  limit,
  showCounter = false,
  sunEditorOptions = {},
  onEditorReady,
  getSunEditorInstance,
  ...inputProps
}) => {
  const [localValue, setLocalValue] = useState(String(value || ""));
  const [charCount, setCharCount] = useState(0);
  const timerRef = useRef({});
  const editorRef = useRef();

  useEditorInitialization(getSunEditorInstance, onEditorReady, editorRef);

  const handler = useMemo(
    () =>
      createOnChange({ onChange, handleChange }, { omitControlChars, name }),
    [onChange, handleChange, omitControlChars, name]
  );

  useEffect(() => {
    syncValueWithLimit({ value, limit, setLocalValue, setCharCount, handler });
  }, [value, limit, handler]);

  const isLabeledText = useLabeledText(ignoreLabeledText);

  if (isLabeledText) {
    return <DisabledInput label={label} value={value} />;
  }

  const editorOptions = buildEditorOptions({
    minHeight,
    placeholder,
    sunEditorOptions,
    limit,
  });

  return (
    <InputLoader ignoreCaption={ignoreCaption}>
      <div>
        {renderLabel(label, error, required)}
        <StyledEditorWrapper
          error={error}
          minHeight={minHeight}
          required={required}
        >
          <SunEditor
            {...inputProps}
            ref={editorRef}
            name={name}
            setContents={_isNil(localValue) ? "" : String(localValue || "")}
            onChange={handleLocalChange({
              setLocalValue,
              avoidDebounce,
              avoidOnChange,
              handler,
              timerRef,
              delay,
              isValid,
              setCharCount,
            })}
            onPaste={handlePaste({ limit, setCharCount })}
            onBlur={handleBlur({
              setLocalValue,
              handler,
              onBlur,
              timerRef,
              omitControlChars,
              name,
            })}
            setOptions={editorOptions}
            disable={isDisabled}
          />
        </StyledEditorWrapper>
        <HelperText error={error}>
          <HelperTextContent>
            {getHelperText({ ignoreCaption, helperText, error, errorMessage })}
          </HelperTextContent>
          {renderCharacterCounter(showCounter, limit, charCount)}
        </HelperText>
      </div>
    </InputLoader>
  );
};

RichTextInputWithError.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  placeholder: PropTypes.string,
  value: PropTypes.string,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  ignoreCaption: PropTypes.bool,
  helperText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  handleChange: PropTypes.func,
  labeledTextProps: PropTypes.shape({}),
  ignoreLabeledText: PropTypes.bool,
  avoidDebounce: PropTypes.bool,
  delay: PropTypes.number,
  onBlur: PropTypes.func,
  isDisabled: PropTypes.bool,
  avoidOnChange: PropTypes.bool,
  isValid: PropTypes.func,
  omitControlChars: PropTypes.bool,
  minHeight: PropTypes.string,
  required: PropTypes.bool,
  limit: PropTypes.number,
  showCounter: PropTypes.bool,
  sunEditorOptions: PropTypes.shape({}),
  onEditorReady: PropTypes.func,
  getSunEditorInstance: PropTypes.func,
};

export default RichTextInputWithError;
